import type { ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'
import { useT } from '@/shared/i18n'

export interface EmptyStateProps {
  title: string
  description?: ReactNode
  /** CTA principal: crear el primer registro. */
  action?: ReactNode
  icon?: ReactNode
  className?: string
}

/**
 * Vacio REAL: no hay registros todavia. Distinto de `NoResultsState`, que es
 * "hay datos pero tus filtros no matchean". Confundirlos hace que el usuario
 * cree duplicados porque cree que no existe lo que ya cargó.
 */
export function EmptyState({ title, description, action, icon, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'hairline flex flex-col items-center justify-center gap-2 rounded-[var(--radius-card)]',
        'bg-surface-1 px-6 py-10 text-center',
        className,
      )}
    >
      {icon ? <div className="text-text-3">{icon}</div> : null}
      <p className="font-display text-base text-text-1">{title}</p>
      {description ? <p className="max-w-[46ch] text-xs text-text-2">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  )
}

export interface NoResultsStateProps {
  onClearFilters?: () => void
  className?: string
}

/** Tercer estado de toda lista: hay datos, pero el filtro no deja ver ninguno. */
export function NoResultsState({ onClearFilters, className }: NoResultsStateProps) {
  const t = useT()

  return (
    <EmptyState
      className={className}
      title={t('common.state.empty')}
      description={t('common.state.noResultsDescription')}
      action={
        onClearFilters ? (
          <button
            type="button"
            onClick={onClearFilters}
            className={cn(
              'hairline h-7 rounded-[var(--radius-control)] px-3 text-xs text-text-1',
              'transition-colors duration-[var(--motion-fast)] hover:bg-surface-2',
            )}
          >
            {t('common.action.clearFilters')}
          </button>
        ) : null
      }
    />
  )
}
